import type { Translations } from "@/src/i18n/translations";
import { ApiError } from "@/src/libs/ApiError";
import { isTimeoutError } from "@/src/libs/fetchWithTimeout";

/** Une clé du scope `errors` du dictionnaire. */
export type ApiErrorMessageKey = keyof Translations["errors"];

/**
 * De quoi décrire une erreur d'API à l'utilisateur.
 *
 * Deux cas, et ils ne se traduisent pas pareil :
 *
 * - `{ key }` — un message générique, qui existe dans les deux langues ;
 * - `{ code }` — le **code métier du serveur** (`INVALID_CREDENTIALS`…),
 *   affiché tel quel. Il n'est pas traduisible : il ne vient pas du
 *   dictionnaire mais de l'API, et c'est justement ce qui le rend utile à un
 *   administrateur qui devra le retrouver dans les journaux.
 */
export type ApiErrorMessage = { key: ApiErrorMessageKey } | { code: string };

/**
 * Classe une erreur remontée par la couche API.
 *
 * Séparé de l'affichage exprès : la logique de classement se teste sans monter
 * de composant, et `useAppToast` n'a plus qu'à traduire le résultat.
 */
export function apiErrorMessage(error: unknown): ApiErrorMessage {
  // Le dépassement de délai se distingue d'un refus du serveur : « réessaie »
  // n'a de sens que dans ce cas-là.
  if (isTimeoutError(error)) return { key: "timeout" };

  const apiError = ApiError.from(error);
  if (!apiError) return { key: "unexpected" };

  if (apiError.status === 401) return { key: "unauthorized" };
  if (apiError.status === 403) return { key: "forbidden" };
  if (apiError.status >= 500) return { key: "server" };

  return apiError.code ? { code: apiError.code } : { key: "refused" };
}
